/**
 * Angular service for the Bookmarks (Reading List) API
 *
 * @remarks
 * Provides all bookmark management operations:
 * - Add / remove individual bookmarks
 * - Toggle a bookmark (add if absent, remove if present)
 * - Load paginated bookmarks for the current user in an org
 *
 * Signal-based reactive state gives consuming components an O(1)
 * `isBookmarked()` check and reactive `bookmarks` / `pagination` signals
 * without needing `ChangeDetectorRef` in `OnPush` components.
 *
 * Optimistic updates are applied immediately and rolled back on API failure,
 * giving the UI instant feedback.
 *
 * Authentication is handled by the `authInterceptor` in the consuming
 * application — this service sends no auth headers directly.
 *
 * @example
 * ```typescript
 * // app.config.ts
 * providers: [
 *   provideBookmarkService({ apiBaseUrl: environment.cmsApiBaseUrl })
 * ]
 *
 * // In a component
 * readonly bookmarkSvc = inject(BookmarkService);
 *
 * ngOnInit() {
 *   this.bookmarkSvc.loadBookmarks(this.orgId).subscribe();
 * }
 *
 * isBookmarked(postId: string): boolean {
 *   return this.bookmarkSvc.isBookmarked(postId);
 * }
 * ```
 *
 * @see BOOKMARKS_ANGULAR_SERVICE.md for full API reference
 */

import {
  Injectable,
  InjectionToken,
  computed,
  inject,
  signal
} from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, catchError, tap, throwError } from 'rxjs';
import {
  AddBookmarkRequest,
  Bookmark,
  BookmarkActionResponse,
  BookmarksState,
  PaginatedBookmarks
} from '../models/bookmark.model';
import { PaginationMeta } from '../models/api-response.model';

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

/**
 * Configuration interface for BookmarkService
 */
export interface BookmarkConfig {
  /**
   * Base URL for the API (without trailing slash)
   * @example 'http://localhost:3001'
   * @example 'https://api.example.com'
   */
  apiBaseUrl: string;

  /**
   * Enable debug logging to the console
   * @default false
   */
  debug?: boolean;
}

/**
 * Injection token for BookmarkService configuration.
 *
 * Provide this token in your `app.config.ts`:
 * ```typescript
 * {
 *   provide: BOOKMARK_API_CONFIG,
 *   useValue: { apiBaseUrl: environment.cmsApiBaseUrl }
 * }
 * ```
 * Or use the `provideBookmarkService()` helper.
 */
export const BOOKMARK_API_CONFIG = new InjectionToken<BookmarkConfig>(
  'BOOKMARK_API_CONFIG',
  {
    providedIn: 'root',
    factory: () => {
      console.warn(
        '[BookmarkService] BOOKMARK_API_CONFIG not provided. ' +
        'Using empty apiBaseUrl. Provide the token or call provideBookmarkService().'
      );
      return { apiBaseUrl: '', debug: false };
    }
  }
);

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

/**
 * Service for managing user bookmarks / reading list.
 *
 * Holds signal-based reactive state so consuming components simply read
 * signals — no manual change detection required.
 */
@Injectable({ providedIn: 'root' })
export class BookmarkService {
  private readonly _http = inject(HttpClient);
  private readonly _config = inject(BOOKMARK_API_CONFIG);
  private readonly _baseUrl = `${this._config.apiBaseUrl}/api/bookmarks`;

  // -------------------------------------------------------------------------
  // Reactive state
  // -------------------------------------------------------------------------

  private readonly _state = signal<BookmarksState>({
    bookmarks: [],
    bookmarkedPostIds: new Set<string>(),
    pagination: null,
    loading: false,
    error: null
  });

  /** Currently loaded bookmark documents */
  readonly bookmarks = computed(() => this._state().bookmarks);

  /** Pagination metadata for the last loaded page */
  readonly pagination = computed(() => this._state().pagination);

  /** True while any HTTP request initiated by this service is in flight */
  readonly loading = computed(() => this._state().loading);

  /** Error message from the most recent failed request, or null */
  readonly error = computed(() => this._state().error);

  /**
   * Returns true when the given post ID is present in the current user's
   * bookmarked set.  O(1) look-up against the in-memory Set.
   *
   * @param postId - MongoDB ObjectId string of the blog post
   */
  isBookmarked(postId: string): boolean {
    return this._state().bookmarkedPostIds.has(postId);
  }

  // -------------------------------------------------------------------------
  // API methods
  // -------------------------------------------------------------------------

  /**
   * Fetch the authenticated user's bookmarks for the given organisation.
   * Populates `bookmarks`, `bookmarkedPostIds`, and `pagination` signals.
   *
   * @param orgId - Organisation MongoID
   * @param page  - 1-based page number (default 1)
   * @param limit - Items per page, max 50 (default 20)
   */
  loadBookmarks(
    orgId: string,
    page = 1,
    limit = 20
  ): Observable<PaginatedBookmarks> {
    const params = new HttpParams()
      .set('orgId', orgId)
      .set('page', String(page))
      .set('limit', String(limit));

    this._patchState({ loading: true, error: null });
    this._log('loadBookmarks', { orgId, page, limit });

    return this._http
      .get<PaginatedBookmarks>(this._baseUrl, { params })
      .pipe(
        tap((response) => {
          const ids = new Set(response.data.map((b) => b.postId._id));
          this._patchState({
            bookmarks: response.data,
            bookmarkedPostIds: ids,
            pagination: response.pagination,
            loading: false
          });
          this._log('loadBookmarks — success', response.pagination);
        }),
        catchError((err: unknown) => {
          const message = this._extractMessage(err, 'Failed to load bookmarks');
          this._patchState({ loading: false, error: message });
          this._log('loadBookmarks — error', message);
          return throwError(() => err);
        })
      );
  }

  /**
   * Add a bookmark for the given post + org.
   *
   * Applies an optimistic update (`bookmarkedPostIds`) immediately and rolls
   * back if the request fails.
   *
   * @param request - `{ postId, orgId }`
   */
  addBookmark(request: AddBookmarkRequest): Observable<BookmarkActionResponse> {
    // Optimistic update
    this._patchState({
      bookmarkedPostIds: new Set([
        ...this._state().bookmarkedPostIds,
        request.postId
      ])
    });
    this._log('addBookmark — optimistic', request.postId);

    return this._http
      .post<BookmarkActionResponse>(this._baseUrl, request)
      .pipe(
        tap(() => this._log('addBookmark — confirmed', request.postId)),
        catchError((err: unknown) => {
          // Roll back
          const ids = new Set(this._state().bookmarkedPostIds);
          ids.delete(request.postId);
          this._patchState({
            bookmarkedPostIds: ids,
            error: this._extractMessage(err, 'Failed to add bookmark')
          });
          this._log('addBookmark — rolled back', request.postId);
          return throwError(() => err);
        })
      );
  }

  /**
   * Remove the bookmark for the given post in the specified org.
   *
   * Applies an optimistic removal from both `bookmarkedPostIds` and the
   * `bookmarks` list; rolls back the `error` state on failure (the list
   * will be stale — call `loadBookmarks()` to resync if needed).
   *
   * @param postId - MongoDB ObjectId string of the blog post
   * @param orgId  - Organisation MongoID
   */
  removeBookmark(
    postId: string,
    orgId: string
  ): Observable<BookmarkActionResponse> {
    // Optimistic update
    const ids = new Set(this._state().bookmarkedPostIds);
    ids.delete(postId);
    this._patchState({
      bookmarkedPostIds: ids,
      bookmarks: this._state().bookmarks.filter(
        (b) => b.postId._id !== postId
      )
    });
    this._log('removeBookmark — optimistic', postId);

    const params = new HttpParams().set('orgId', orgId);
    return this._http
      .delete<BookmarkActionResponse>(`${this._baseUrl}/${postId}`, { params })
      .pipe(
        tap(() => this._log('removeBookmark — confirmed', postId)),
        catchError((err: unknown) => {
          this._patchState({
            error: this._extractMessage(err, 'Failed to remove bookmark')
          });
          this._log('removeBookmark — error (list may be stale)', postId);
          return throwError(() => err);
        })
      );
  }

  /**
   * Toggle the bookmark state for a post.
   *
   * Delegates to `addBookmark()` or `removeBookmark()` depending on the
   * current `isBookmarked()` result.
   *
   * @param postId - MongoDB ObjectId string of the blog post
   * @param orgId  - Organisation MongoID
   * @returns Observable that emits the API action response
   */
  toggleBookmark(
    postId: string,
    orgId: string
  ): Observable<BookmarkActionResponse> {
    return this.isBookmarked(postId)
      ? this.removeBookmark(postId, orgId)
      : this.addBookmark({ postId, orgId });
  }

  /**
   * Reset all state.
   *
   * Call this on user logout or when switching organisations to prevent
   * stale bookmarks from being shown for the wrong session.
   */
  reset(): void {
    this._patchState({
      bookmarks: [],
      bookmarkedPostIds: new Set<string>(),
      pagination: null,
      loading: false,
      error: null
    });
    this._log('state reset');
  }

  // -------------------------------------------------------------------------
  // Private helpers
  // -------------------------------------------------------------------------

  private _patchState(patch: Partial<BookmarksState>): void {
    this._state.update((s) => ({ ...s, ...patch }));
  }

  private _extractMessage(err: unknown, fallback: string): string {
    if (err instanceof Error) return err.message;
    if (
      typeof err === 'object' &&
      err !== null &&
      'message' in err &&
      typeof (err as Record<string, unknown>)['message'] === 'string'
    ) {
      return (err as Record<string, unknown>)['message'] as string;
    }
    return fallback;
  }

  private _log(message: string, ...args: unknown[]): void {
    if (this._config.debug) {
      console.log(`[BookmarkService] ${message}`, ...args);
    }
  }
}
